import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Card } from 'react-bootstrap';
import { getThemeColors } from '../../utils/chartTheme';

/** Farben: Soll (grau hell) vs Ist (Solar-Gelb) */
const FARBE_SOLL       = 'rgba(108, 117, 125, 0.50)';
const FARBE_SOLL_BORDER = 'rgba(108, 117, 125, 0.75)';
const FARBE_IST        = 'rgba(245, 158, 11, 0.75)';
const FARBE_IST_BORDER  = 'rgba(217, 117, 6, 0.50)';

/**
 * Soll-vs-Ist Kachel fuer PV-Jahresvergleich-Seite.
 * Gruppiertes Balkendiagramm: Soll (grau) neben Ist (Solar-Gelb).
 * Zusammenfassungszeile unten mit Gesamt-Soll, Gesamt-Ist und Abweichung.
 *
 * @param {object} props
 * @param {Array}  props.daten — monatsertragVergleich[] von der API (fuer das geladene Jahr)
 * @param {number} props.jahr  — aktuell geladenes Jahr
 * @param {Array}  props.verfuegbareJahre — Jahre die in den Daten vorhanden sind
 * @param {function} [props.onLoadYear] — Callback(year) zum Laden eines anderen Jahres
 */
export default function SollVsIstKachel({ daten, jahr, verfuegbareJahre, onLoadYear }) {

    if (!daten || daten.length === 0) {
        return (
            <Card className="chart-card mb-3 soll-ist-kachel">
                <Card.Body>
                    <div className="text-muted text-center py-4">
                        <i className="bi bi-info-circle me-1" />
                        {' '}Keine Soll-vs-Ist Daten fuer {jahr} verfuegbar.
                    </div>
                </Card.Body>
            </Card>
        );
    }

    var zusammenfassung = berechneZusammenfassung(daten);

    return (
        <Card className="chart-card mb-3 soll-ist-kachel">
            <Card.Body>
                {/* Titel + Jahrswahl */}
                <h6 className="card-title d-flex justify-content-between align-items-center flex-wrap">
                    <span>
                        <i className="bi bi-bar-chart-line me-1" />
                        Soll-vs-Ist Monatsvergleich
                    </span>
                    <div className="d-flex gap-1 flex-wrap">
                        {verfuegbareJahre && verfuegbareJahre.map(j => (
                            <button
                                key={j}
                                type="button"
                                className={`btn btn-sm ${j === jahr ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => onLoadYear && onLoadYear(j)}
                            >
                                {j}
                            </button>
                        ))}
                    </div>
                </h6>

                {/* Gruppiertes Balkendiagramm */}
                <Bar
                    data={buildChartData(daten)}
                    options={buildChartOptions()}
                />

                {/* Zusammenfassung */}
                {zusammenfassung && (
                    <div className="soll-ist-zusammenfassung mt-3 text-center small">
                        <span className="me-3">
                            <strong>Gesamt Soll:</strong>{' '}
                            {formatNumber(zusammenfassung.gesamtSoll, 0)} kWh
                        </span>
                        <span className="me-3">
                            <strong>Gesamt Ist:</strong>{' '}
                            {formatNumber(zusammenfassung.gesamtIst, 0)} kWh
                        </span>
                        <span className={`fw-bold ${getAbweichungClass(zusammenfassung.abweichungProzent)}`}>
                            Abweichung:{' '}
                            {zusammenfassung.abweichungProzent >= 0 ? '+' : ''}
                            {formatNumber(zusammenfassung.abweichungProzent, 1)}%
                        </span>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}

/* ------------------------------------------------------------------ */
/*  Zusammenfassung                                                    */
/* ------------------------------------------------------------------ */

function berechneZusammenfassung(daten) {
    var gesamtSoll = 0;
    var gesamtIst = 0;

    for (var i = 0; i < daten.length; i++) {
        if (daten[i].sollKwh != null) gesamtSoll += daten[i].sollKwh;
        if (daten[i].istKwh != null) gesamtIst += daten[i].istKwh;
    }

    var abwPct = gesamtSoll > 0 ? ((gesamtIst - gesamtSoll) / gesamtSoll) * 100 : null;

    return {
        gesamtSoll: Math.round(gesamtSoll),
        gesamtIst: Math.round(gesamtIst),
        abweichungProzent: abwPct != null ? Math.round(abwPct * 10) / 10 : null,
    };
}

/* ------------------------------------------------------------------ */
/*  Chart-Daten                                                        */
/* ------------------------------------------------------------------ */

function buildChartData(daten) {
    var labels = [];
    var sollData = [];
    var istData = [];
    var diffLabels = [];

    for (var i = 0; i < daten.length; i++) {
        var item = daten[i];
        labels.push(item.monatsname || MONATSNAME[item.monat - 1]);
        sollData.push(item.sollKwh != null ? Math.round(item.sollKwh) : null);
        istData.push(item.istKwh != null ? Math.round(item.istKwh) : null);

        if (item.abweichungProzent != null) {
            var sign = item.abweichungProzent >= 0 ? '+' : '';
            diffLabels.push(sign + item.abweichungProzent.toFixed(1) + '%');
        } else {
            diffLabels.push('');
        }
    }

    return {
        labels: labels,
        datasets: [
            {
                label: 'Soll (kWh)',
                data: sollData,
                backgroundColor: FARBE_SOLL,
                borderColor: FARBE_SOLL_BORDER,
                borderWidth: 1,
                borderRadius: 3,
                barPercentage: 0.70,
                categoryPercentage: 0.80,
            },
            {
                label: 'Ist (kWh)',
                data: istData,
                backgroundColor: FARBE_IST,
                borderColor: FARBE_IST_BORDER,
                borderWidth: 1,
                borderRadius: 3,
                barPercentage: 0.70,
                categoryPercentage: 0.80,
            },
        ],
    };
}

/* ------------------------------------------------------------------ */
/*  Chart-Optionen                                                     */
/* ------------------------------------------------------------------ */

function buildChartOptions() {
    var tc = getThemeColors();

    return {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2.0,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            datalabels: { display: false },
            tooltip: {
                backgroundColor: 'rgba(33, 37, 41, 0.92)',
                titleColor: '#e4e6ea',
                bodyColor: '#e4e6ea',
                padding: 10,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        var val = context.raw;
                        if (val === null || val === undefined) return '';
                        return context.dataset.label + ': ' + formatNumber(val, 0) + ' kWh';
                    },
                },
            },
            legend: { display: true, position: 'bottom' },
        },
        scales: {
            x: {
                grid: { color: tc.gridColor, drawBorder: false },
                ticks: {
                    autoSkip: false,
                    maxRotation: 45,
                    color: tc.textColor,
                },
            },
            y: {
                grid: { color: tc.gridColor, drawBorder: false },
                beginAtZero: true,
                ticks: {
                    color: tc.textColor,
                    callback: function (v) { return formatNumber(v, 0); },
                },
                title: {
                    display: true,
                    text: 'kWh',
                    color: tc.textColor,
                },
            },
        },
    };
}

/* ------------------------------------------------------------------ */
/*  Hilfsfunktionen                                                    */
/* ------------------------------------------------------------------ */

var MONATSNAME = [
    'Januar', 'Februar', 'Maerz', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

function formatNumber(val, decimals) {
    if (val === null || val === undefined) return '—';
    var num = Number(val);
    if (isNaN(num)) return '—';
    try {
        return num.toLocaleString('de-DE', {
            minimumFractionDigits: decimals || 0,
            maximumFractionDigits: decimals || 0,
        });
    } catch (_) {
        return String(num);
    }
}

function getAbweichungClass(pct) {
    if (pct === null || pct === undefined) return '';
    if (pct >= -1.2) return 'text-success';
    if (pct >= -3.0) return 'text-warning';
    return 'text-danger';
}
